import { ANNUAL_DISCOUNT_PERCENT } from 'definitions/billing-plans';
import { cn } from 'helpers/cn';
import { useState } from 'react';
import { BILLING_PLANS } from 'definitions';
import { usePlanPicker } from '@/state/billing/usePlanPicker';
import { useBillingAllowance } from '@/state/billing/useBillingAllowance';
import { formatBillingDate } from '@/state/billing/billing-format';
import { FOCUS_RING, Panel, PillTabs } from '../../ConsolePanel';
import { KeepPlanDialog } from './KeepPlanDialog';
import { PlanCards } from './PlanCards';
import { PlanSummary } from './PlanSummary';

type PlansPanelProps = {
  /** An agency's client whose plan this is; the caller's own account when omitted. */
  accountId: number | null;
  accountName: string | null;
};

const INTERVAL_OPTIONS = [
  { label: 'Monthly', value: 'month' },
  { label: `Yearly · save ${ANNUAL_DISCOUNT_PERCENT}%`, value: 'year' },
] as const;

/**
 * Settings > Billing's plan picker: where an account without a plan starts one, and where one
 * with a plan changes it. It is the sign-up's own plan cards, and picking one only selects it:
 * a summary says what accepting does (what is charged, and when it applies), and the button on
 * the summary is what does it. Shown by useBillingSettings only to an Owner or Admin of whoever
 * pays for the account on show: the account itself, or the agency for one of its clients (each
 * client has its own plan).
 */
export const PlansPanel = ({ accountId, accountName }: PlansPanelProps) => {
  const picker = usePlanPicker({ accountId });
  const who = accountId ? (accountName ?? 'this client') : null;
  const { summary, pendingChange } = picker;
  const { allowance, keepPlan } = useBillingAllowance({ accountId });
  const [confirmingKeep, setConfirmingKeep] = useState(false);
  // A scheduled move up is not just cancelled: staying on the plan holds the account to its sessions, so it asks.
  const pendingIsUpgrade =
    !!pendingChange && !!allowance && BILLING_PLANS.indexOf(pendingChange.plan) > BILLING_PLANS.indexOf(allowance.plan);

  return (
    <Panel
      tourAnchor="billing-plans"
      title={who ? `Plans for ${who}` : 'Plans'}
      description={
        picker.hasPlan
          ? 'Choose a plan to see what changing to it does. Nothing happens until you accept.'
          : picker.trialAvailable
            ? 'Every plan starts with a 12-day free trial. Every plan includes unlimited websites, users and experiments.'
            : 'Every plan includes unlimited websites, users and experiments.'
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-[7px]">
          <span className="micro-label">Billing</span>
          <div className="whitespace-nowrap">
            <PillTabs
              options={INTERVAL_OPTIONS}
              value={picker.interval}
              onChange={(value) => picker.setInterval(value === 'month' ? 'month' : 'year')}
            />
          </div>
        </div>

        <PlanCards
          selected={picker.selected}
          current={picker.currentOnShow}
          scheduled={picker.scheduledOnShow}
          interval={picker.interval}
          onSelect={picker.select}
        />

        {picker.pendingChange && (
          <p className="supporting m-0">
            The plan changes to the {picker.pendingChange.plan} plan (
            {picker.pendingChange.interval === 'year' ? 'annual' : 'monthly'}) on{' '}
            {formatBillingDate(picker.pendingChange.appliesAt)}.{' '}
            <button
              type="button"
              onClick={pendingIsUpgrade ? () => setConfirmingKeep(true) : picker.cancelPendingChange}
              disabled={picker.isCancelling}
              className={cn('text-foreground underline underline-offset-4 disabled:opacity-60', FOCUS_RING)}
            >
              Keep the current plan
            </button>
          </p>
        )}

        {pendingChange && allowance && pendingIsUpgrade && (
          <KeepPlanDialog
            open={confirmingKeep}
            plan={allowance.plan}
            nextPlan={pendingChange.plan}
            appliesAt={pendingChange.appliesAt}
            included={allowance.included}
            windowEnd={allowance.windowEnd}
            onConfirm={() => {
              keepPlan();
              setConfirmingKeep(false);
            }}
            onCancel={() => setConfirmingKeep(false)}
          />
        )}

        {summary && (
          <PlanSummary
            label="Your change"
            title={summary.title}
            line={summary.line}
            rows={summary.rows}
            ctaLabel={summary.ctaLabel}
            note={summary.note}
            showPaymentMethods={!picker.hasPlan && picker.canAccept}
            disabled={!picker.canAccept}
            isPending={picker.isAccepting}
            onContinue={picker.accept}
            secondary={{ label: 'Cancel', onClick: picker.clearChoice }}
          />
        )}
      </div>
    </Panel>
  );
};
